import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FEATURE_INTEREST, type FeatureInterestKey } from '@erp/shared-types';

import { orpc, rpcClient } from '@/api/rpc/client';
import { useMyFeedbackForSubject } from '@/api/rpc/hooks/core/user-feedback/useMyFeedbackForSubject';
import { useToast } from '@/context/ToastContext';

export interface FeatureInterest {
  /** True once this user has asked to be alerted about `featureKey`. */
  isRegistered: boolean;
  /** What the user told us they'd use the feature for, if anything. */
  savedNote: string | null;
  isLoading: boolean;
  isSubmitting: boolean;
  register: (note?: string | null) => Promise<void>;
}

/**
 * Reads and writes one user's "alert me when this ships" signal for a previewed
 * feature, on top of the shared user-feedback API. The row is keyed by
 * (org, subject, scoreName, user), so re-clicking the CTA — or coming back later
 * to add a note — updates in place instead of piling up duplicates, and the
 * stored row is what tells us who wants the feature.
 */
export function useFeatureInterest(
  featureKey: FeatureInterestKey,
): FeatureInterest {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { feedbackByScoreName, isLoading } = useMyFeedbackForSubject({
    subjectType: FEATURE_INTEREST.subjectType,
    subjectId: featureKey,
  });

  const existing = feedbackByScoreName?.[FEATURE_INTEREST.scoreName];

  const mutation = useMutation({
    mutationFn: async (note: string | null) => {
      return await rpcClient.core.userFeedback.submit({
        subjectType: FEATURE_INTEREST.subjectType,
        subjectId: featureKey,
        scoreName: FEATURE_INTEREST.scoreName,
        value: { dataType: 'BOOLEAN', booleanValue: true },
        comment: note,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.core.userFeedback.getMyForSubject.key({
          input: {
            subjectType: FEATURE_INTEREST.subjectType,
            subjectId: featureKey,
          },
        }),
      });
    },
    onError: (error: Error) => {
      showToast({
        title: 'Could not save your request',
        message: error.message,
        color: 'error',
      });
    },
  });

  return {
    isRegistered: existing?.booleanValue === true,
    savedNote: existing?.comment ?? null,
    isLoading,
    isSubmitting: mutation.isPending,
    register: async (note) => {
      const trimmed = note?.trim();
      await mutation.mutateAsync(trimmed ? trimmed : null);
    },
  };
}
